import React, { Suspense, useState } from "react";
import { useTranslation } from "react-i18next";
import { ErrorBoundary } from "react-error-boundary";

import { HvDialog } from "@hitachivantara/uikit-react-core";

import { DialogContentProps } from "../common";
import DialogContent from "./DialogContent";

import useStyles from "./styles";

export interface SmsDialogProps extends DialogContentProps {
  isOpen: boolean;
}

const SmsDialog: React.FC<SmsDialogProps> = ({
  isOpen,
  onCancel,
  onApply,
  actionType,
  initialValues,
}) => {
  const onCloseHandler = (
    _event: unknown,
    reason: "backdropClick" | "escapeKeyDown"
  ) => {
    if (reason !== "backdropClick") {
      onCancel?.();
    }
  };

  const classes = useStyles();
  const { t } = useTranslation("smsDialog");

  // fk_notification_template_id (shouldn't but) might be invalid
  // useNotificationTemplate is using suspense, so we need to use an ErrorBoundary
  // (https://swr.vercel.app/docs/suspense)
  // in this case, we retry re-rendering without loading the selected template data.
  // if it fails again (e.g. in the call to get the template list),
  // we simply call onCancel to close the dialog.
  const [errorOpeningContent, setErrorOpeningContent] = useState(false);
  const onError = () => {
    if (!errorOpeningContent) {
      // first time
      setErrorOpeningContent(true);
    } else {
      onCancel?.();
    }
  };

  return (
    <HvDialog
      buttonTitle={t("buttons.close")}
      classes={{ paper: classes.paper }}
      open={isOpen}
      onClose={onCloseHandler}
      fullWidth
      maxWidth="md"
    >
      <ErrorBoundary
        fallback={<></>}
        onError={onError}
        resetKeys={[errorOpeningContent]}
      >
        <Suspense fallback={<></>}>
          {isOpen && (
            // this separation is merely for enclosing the api calls in the dialog's <Suspense>, rather than the page's
            <DialogContent
              onCancel={onCancel}
              onApply={onApply}
              actionType={actionType}
              initialValues={initialValues}
              skipTemplateLoad={errorOpeningContent}
            />
          )}
        </Suspense>
      </ErrorBoundary>
    </HvDialog>
  );
};

export default SmsDialog;
